import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, UserPlus, Leaf, Search, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from './Logo';
import { useToast } from '../hooks/use-toast';

const Inscription = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sirenLoading, setSirenLoading] = useState(false);
  const [sirenFound, setSirenFound] = useState(false);
  const [sirenRadie, setSirenRadie] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressValid, setAddressValid] = useState(false);
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    adresse: '',
    societe: '',
    adresseSociete: '',
    siren: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const lookupSiren = async (siren) => {
    if (siren.length !== 9) {
      setSirenFound(false);
      return;
    }
    setSirenLoading(true);
    try {
      const res = await axios.get(`https://recherche-entreprises.api.gouv.fr/search?q=${siren}`);
      const results = res.data?.results || [];
      const match = results.find(r => r.siren === siren);
      if (match) {
        if (match.etat_administratif === 'C') {
          setSirenFound(false);
          setSirenRadie(true);
          return;
        }
        setSirenRadie(false);
        const adresse = match.siege?.adresse || '';
        setFormData(prev => ({
          ...prev,
          societe: match.nom_complet || '',
          adresseSociete: adresse,
        }));
        setSirenFound(true);
      } else {
        setSirenFound(false);
      }
    } catch {
      setSirenFound(false);
    }
    setSirenLoading(false);
  };

  const lookupByName = async (nom) => {
    if (nom.length < 3) return;
    setSirenLoading(true);
    try {
      const res = await axios.get(`https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(nom)}&per_page=1`);
      const results = res.data?.results || [];
      if (results.length > 0) {
        const match = results[0];
        if (match.etat_administratif === 'C') {
          setSirenFound(false);
          setSirenRadie(true);
          return;
        }
        setSirenRadie(false);
        const adresse = match.siege?.adresse || '';
        setFormData(prev => ({
          ...prev,
          siren: match.siren || '',
          societe: match.nom_complet || '',
          adresseSociete: adresse,
        }));
        setSirenFound(true);
      } else {
        setSirenFound(false);
      }
    } catch {
      setSirenFound(false);
    }
    setSirenLoading(false);
  };

  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/[\s.\-]/g, '');
    return /^(0[1-9]\d{8}|\+33[1-9]\d{8}|0033[1-9]\d{8})$/.test(cleaned);
  };

  const searchAddress = async (query) => {
    setFormData({ ...formData, adresse: query });
    setAddressValid(false);
    if (query.length < 5) { setAddressSuggestions([]); return; }
    try {
      const res = await axios.get(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
      setAddressSuggestions(res.data.features || []);
    } catch { setAddressSuggestions([]); }
  };

  const selectAddress = (feature) => {
    setFormData({ ...formData, adresse: feature.properties.label });
    setAddressValid(true);
    setAddressSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidPhone(formData.phone)) {
      toast({
        title: "Erreur",
        description: "Numéro de téléphone invalide. Format attendu : 10 chiffres (06...), +33... ou 0033...",
        variant: "destructive"
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    if (formData.type === 'professionnel' && sirenRadie) {
      toast({
        title: "Entreprise radiée",
        description: "Impossible de créer un compte avec un SIREN d'entreprise radiée",
        variant: "destructive"
      });
      return;
    }

    if (formData.type !== 'professionnel' && !addressValid && formData.adresse) {
      toast({
        title: "Adresse non vérifiée",
        description: "Veuillez sélectionner une adresse dans la liste de suggestions",
        variant: "destructive"
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        type: formData.type,
        adresse: formData.type === 'professionnel' ? formData.adresseSociete : formData.adresse,
        societe: formData.societe,
        siren: formData.siren,
        adresse_societe: formData.adresseSociete
      });
      
      toast({
        title: "Compte créé !",
        description: "Bienvenue sur Location Benne Chantier",
      });
      
      // Rediriger vers le compte
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue lors de l'inscription",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center mb-4 hover:opacity-80 transition-opacity"
            data-testid="inscription-logo-home"
          >
            <img src="/logo-ler.png" alt="Location Benne Chantier" className="h-16 w-auto object-contain" />
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Créer un compte
          </h1>
          <p className="text-gray-600">
            Remplissez le formulaire pour accéder aux prix en temps réel
          </p>
        </div>

        <Card className="p-8 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Type de client */}
            <div>
              <Label className="text-base font-semibold">Vous êtes *</Label>
              <Select 
                value={formData.type} 
                onValueChange={(value) => setFormData({ ...formData, type: value })}
                required
              >
                <SelectTrigger className="mt-2 h-12">
                  <SelectValue placeholder="Sélectionnez" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="particulier">Un particulier</SelectItem>
                  <SelectItem value="professionnel">Un professionnel</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* SIREN (professionnel uniquement) */}
            {formData.type === 'professionnel' && (
              <div>
                <Label htmlFor="siren" className="text-base font-semibold">Numéro SIREN</Label>
                <div className="relative mt-2">
                  <Input
                    id="siren"
                    className="h-12 pr-12"
                    value={formData.siren}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setFormData({ ...formData, siren: val });
                      setSirenFound(false);
                      if (val.length === 9) lookupSiren(val);
                    }}
                    placeholder="9 chiffres — ex: 443061841"
                    maxLength={9}
                    data-testid="inscription-siren"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {sirenLoading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
                    {!sirenLoading && sirenFound && <CheckCircle className="h-5 w-5 text-green-600" />}
                  </div>
                </div>
                {sirenFound && formData.societe && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Société trouvée : <strong>{formData.societe}</strong>
                  </p>
                )}
                {formData.siren.length === 9 && !sirenLoading && !sirenFound && !sirenRadie && (
                  <p className="text-xs text-red-500 mt-1">SIREN introuvable — vérifiez le numéro</p>
                )}
                {sirenRadie && (
                  <p className="text-xs text-red-500 mt-1 font-semibold">Cette entreprise est radiée (cessation d'activité). Impossible de créer un compte avec ce SIREN.</p>
                )}
              </div>
            )}

            {/* Nom de société (professionnel uniquement) */}
            {formData.type === 'professionnel' && (
              <div>
                <Label htmlFor="societe" className="text-base font-semibold">Nom de votre société *</Label>
                <div className="relative mt-2">
                  <Input
                    id="societe"
                    className="h-12 pr-12"
                    value={formData.societe}
                    onChange={(e) => {
                      setFormData({ ...formData, societe: e.target.value });
                      setSirenFound(false);
                    }}
                    placeholder="Tapez le nom pour rechercher automatiquement"
                    required
                    data-testid="inscription-societe"
                  />
                  {!sirenFound && formData.societe.length >= 3 && (
                    <button
                      type="button"
                      onClick={() => lookupByName(formData.societe)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-green-600 text-white p-1.5 rounded-md hover:bg-green-700 transition-colors"
                      data-testid="search-societe-btn"
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {!sirenFound && (
                  <p className="text-xs text-gray-500 mt-1">Entrez le nom puis cliquez sur la loupe pour retrouver le SIREN et l'adresse</p>
                )}
              </div>
            )}

            {/* Adresse de la société (professionnel uniquement) */}
            {formData.type === 'professionnel' && (
              <div>
                <Label htmlFor="adresseSociete" className="text-base font-semibold">Adresse de la société *</Label>
                <Input
                  id="adresseSociete"
                  className="mt-2 h-12"
                  value={formData.adresseSociete}
                  onChange={(e) => setFormData({ ...formData, adresseSociete: e.target.value })}
                  placeholder="Adresse du siège social"
                  required
                  data-testid="inscription-adresse-societe"
                />
              </div>
            )}

            {/* Nom */}
            <div>
              <Label htmlFor="name" className="text-base font-semibold">Nom et prénom *</Label>
              <Input
                id="name"
                className="mt-2 h-12"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jean Dupont"
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-base font-semibold">Email *</Label>
              <Input
                id="email"
                type="email"
                className="mt-2 h-12"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="jean.dupont@email.fr"
                required
              />
            </div>

            {/* Adresse */}
            {formData.type !== 'professionnel' && (
            <div className="relative">
              <Label htmlFor="adresse" className="text-base font-semibold">Adresse *</Label>
              <Input
                id="adresse"
                className={`mt-2 h-12 ${addressValid ? 'border-green-500' : ''}`}
                value={formData.adresse}
                onChange={(e) => searchAddress(e.target.value)}
                placeholder="Commencez à taper votre adresse..."
                required
              />
              {addressValid && <CheckCircle className="absolute right-3 top-11 h-5 w-5 text-green-500" />}
              {addressSuggestions.length > 0 && (
                <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                  {addressSuggestions.map((feat, i) => (
                    <button
                      key={i}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 border-b last:border-0"
                      onClick={() => selectAddress(feat)}
                    >
                      {feat.properties.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Téléphone */}
            <div>
              <Label htmlFor="phone" className="text-base font-semibold">Téléphone *</Label>
              <Input
                id="phone"
                type="tel"
                className={`mt-2 h-12 ${formData.phone && !isValidPhone(formData.phone) ? 'border-red-400' : formData.phone && isValidPhone(formData.phone) ? 'border-green-400' : ''}`}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="06 12 34 56 78 ou +33 6 12 34 56 78"
                required
              />
              {formData.phone && !isValidPhone(formData.phone) && (
                <p className="text-xs text-red-500 mt-1">Format attendu : 10 chiffres (06..., 07...), +33... ou 0033...</p>
              )}
            </div>

            {/* Mot de passe */}
            <div>
              <Label htmlFor="password" className="text-base font-semibold">Mot de passe *</Label>
              <Input
                id="password"
                type="password"
                className="mt-2 h-12"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Minimum 6 caractères"
                required
              />
            </div>

            {/* Confirmation mot de passe */}
            <div>
              <Label htmlFor="confirmPassword" className="text-base font-semibold">
                Confirmer le mot de passe *
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                className="mt-2 h-12"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Retapez votre mot de passe"
                required
              />
            </div>

            {/* Bouton de soumission */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Création en cours...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-5 w-5" />
                  Créer mon compte
                </>
              )}
            </Button>

            {/* Lien vers connexion */}
            <p className="text-center text-gray-600 mt-4">
              Déjà un compte ?{' '}
              <button
                type="button"
                onClick={() => navigate('/connexion')}
                className="text-green-600 hover:text-green-700 font-semibold"
              >
                Se connecter
              </button>
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Inscription;
